import React from "react";
import SubPageLayout from "../components/SubPageLayout";
import { motion } from "framer-motion";
import {
  NAAVI_DIFFERENTIATOR,
  NAAVI_CORE,
  NAAVI_ENVIRONMENT,
  NAAVI_FACILITATORS,
} from "../data/whyNaaviContent";
import { CheckCircle2, Sparkles } from "lucide-react";

export default function WhyNaavi() {
  return (
    <SubPageLayout
      eyebrow="Why Naavi"
      title={
        <>
          The Naavi <span className="italic font-normal">difference</span>.
        </>
      }
      subtitle="What sets us apart — a life skills-led approach, deeply held values, an inspiring learning environment and the gentle, skilled teachers who bring it all alive."
      accent="cornflower"
      decor="circle"
    >
      {/* DIFFERENTIATOR */}
      <section
        id="differentiator"
        className="py-14 md:py-24 bg-[#FDFDF9] scroll-mt-32"
        data-testid="why-differentiator"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start mb-14">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
                {NAAVI_DIFFERENTIATOR.eyebrow}
              </span>
              <h2 className="font-display text-[32px] md:text-5xl text-[#0F1B47] font-semibold leading-tight mb-6">
                {NAAVI_DIFFERENTIATOR.heading}
              </h2>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="rounded-[24px] overflow-hidden aspect-[3/4]">
                  <img decoding="async"
                    src={NAAVI_DIFFERENTIATOR.image}
                    alt="A Naavi child practicing washing — a daily life skill"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="rounded-[24px] overflow-hidden aspect-[3/4]">
                  <img decoding="async"
                    src={NAAVI_DIFFERENTIATOR.imageSecondary}
                    alt="A Naavi child at the folding station — building self-management"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 space-y-4 text-[#4A5568] text-base md:text-lg leading-relaxed font-light">
              {NAAVI_DIFFERENTIATOR.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          <div className="text-center mb-10">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5]">
              Key focus areas
            </span>
            <h3 className="font-display text-2xl md:text-3xl text-[#0F1B47] font-medium mt-2">
              The Life Skills Programme, in detail
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {NAAVI_DIFFERENTIATOR.pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bento-card bg-[#F5F5F0] rounded-3xl p-7 border border-[#0F1B47]/6"
                data-testid={`life-skill-${i}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-display text-3xl text-[#648DE5] font-semibold">
                    0{i + 1}
                  </span>
                  <h4 className="font-display text-xl text-[#0F1B47] font-medium leading-snug">
                    {p.title}
                  </h4>
                </div>
                <ul className="space-y-2.5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[#4A5568] text-[14.5px] leading-relaxed font-light">
                      <CheckCircle2 size={16} className="text-[#5BC4B8] mt-0.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE VALUES & COMPETENCIES */}
      <section
        id="core"
        className="py-14 md:py-24 bg-[#F5F5F0] scroll-mt-32"
        data-testid="why-core"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
                What we hold dear
              </span>
              <h2 className="font-display text-[32px] md:text-5xl text-[#0F1B47] font-semibold leading-tight">
                Our Core Values <br />& <span className="italic font-normal">Competencies</span>.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-4 text-[#4A5568] text-base leading-relaxed font-light">
              {NAAVI_CORE.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[2px] w-10 bg-[#648DE5]" />
              <h3 className="text-xs font-bold tracking-[0.22em] uppercase text-[#0F1B47]">
                Core Values
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {NAAVI_CORE.values.map((v, i) => (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: i * 0.04 }}
                  className="bento-card bg-[#FDFDF9] rounded-3xl p-6 border border-[#0F1B47]/6"
                  data-testid={`value-${i}`}
                >
                  <div className="w-9 h-9 rounded-full mb-4" style={{ background: v.color }} />
                  <h4 className="font-display text-lg text-[#0F1B47] font-medium mb-2 leading-snug">
                    {v.title}
                  </h4>
                  <p className="text-[#4A5568] text-[13.5px] leading-relaxed font-light">
                    {v.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Core Competencies */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[2px] w-10 bg-[#C6D947]" />
              <h3 className="text-xs font-bold tracking-[0.22em] uppercase text-[#0F1B47]">
                Core Competencies
              </h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {NAAVI_CORE.competencies.map((c, i) => (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: i * 0.04 }}
                  className="bg-[#0F1B47] rounded-3xl p-6 text-[#FDFDF9]"
                  data-testid={`competency-${i}`}
                >
                  <Sparkles size={18} className="text-[#C6D947] mb-3" />
                  <h4 className="font-display text-lg font-medium mb-2 leading-snug">
                    {c.title}
                  </h4>
                  <p className="text-[#FDFDF9]/75 text-[13.5px] leading-relaxed font-light">
                    {c.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LEARNING ENVIRONMENT */}
      <section
        id="environment"
        className="py-14 md:py-24 bg-[#FDFDF9] scroll-mt-32"
        data-testid="why-environment"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start mb-12">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
                Our Learning Environment
              </span>
              <h2 className="font-display text-[32px] md:text-5xl text-[#0F1B47] font-semibold leading-tight mb-6">
                {NAAVI_ENVIRONMENT.heading}
              </h2>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="rounded-[24px] overflow-hidden aspect-[3/4]">
                  <img decoding="async"
                    src={NAAVI_ENVIRONMENT.image}
                    alt="A Naavi child at the cow handwashing station"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="rounded-[24px] overflow-hidden aspect-[3/4]">
                  <img decoding="async"
                    src={NAAVI_ENVIRONMENT.imageSecondary}
                    alt="A Naavi child watering the school garden"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5 text-[#4A5568] text-base md:text-[17px] leading-relaxed font-light">
              {NAAVI_ENVIRONMENT.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-2">
                {NAAVI_ENVIRONMENT.activities.map((a, i) => {
                  const colors = ["#648DE5", "#C6D947", "#5BC4B8", "#3AB0D6", "#648DE5"];
                  return (
                    <div
                      key={a}
                      className="bg-[#F5F5F0] rounded-2xl p-4 flex items-center gap-3 border border-[#0F1B47]/6"
                      data-testid={`activity-${i}`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: colors[i % colors.length] }}
                      />
                      <span className="text-[#0F1B47] font-semibold text-sm">{a}</span>
                    </div>
                  );
                })}
              </div>

              {NAAVI_ENVIRONMENT.more.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FACILITATORS */}
      <section
        id="facilitators"
        className="py-14 md:py-24 bg-[#F5F5F0] scroll-mt-32"
        data-testid="why-facilitators"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl mb-14">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
              Our Facilitators
            </span>
            <h2 className="font-display text-[32px] md:text-5xl text-[#0F1B47] font-semibold leading-tight mb-6">
              The teachers who bring <span className="italic font-normal">it all alive</span>.
            </h2>
            <div className="space-y-4 text-[#4A5568] text-base md:text-lg leading-relaxed font-light">
              {NAAVI_FACILITATORS.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {NAAVI_FACILITATORS.cards.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: i * 0.05 }}
                className="bento-card bg-[#FDFDF9] rounded-3xl overflow-hidden border border-[#0F1B47]/6"
                data-testid={`facilitator-card-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img decoding="async"
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl text-[#0F1B47] font-medium mb-2 leading-snug">
                    {c.title}
                  </h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed font-light">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </SubPageLayout>
  );
}
